// Orders list — dense table, real Vietnamese data, tabular numerics.
// Operate mode: user scans quickly. Status pills are shape+text, not color-only.

import { useEffect, useMemo, useState } from 'react'
import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { get, post, downloadCsv, downloadFile } from '@/lib/api'
import { money, phoneDisplay, shortOrderId } from '@/lib/format'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'
import { ImportOrdersModal } from '@/components/ImportOrdersModal'
import { WaybillZipDialog } from '@/components/orders/WaybillZipDialog'
import { ORDER_FILTER_STATUSES, statusMeta, type StatusIntent } from '@/lib/orderStatus'
import { useIsMobile } from '@/hooks/useIsMobile'
import DataTable, { IndexBadge, type DataTableColumn } from '@/components/ui/data-table'
import { Checkbox } from '@/components/ui/checkbox'
import { PageHeader } from '@/components/ui/page-header'
import { Fab } from '@/components/ui/fab'
import { CalendarDays, Download, Plus, Printer } from 'lucide-react'
import { SearchInput } from '@/components/ui/search-input'
import { StatusPill } from '@/components/ui/status'
import { OrderMoneyCell, type OrderMoneyView } from '@/components/orders/order-money'
import { useUnitCan } from '@/lib/unitAccess'
import { printWaybills } from '@/lib/printWaybills'
import { DateRangeFilter, applyDateFilter, useDateFilterParams } from '@/components/orders/DateRangeFilter'
import { rangeSummary } from '@shoptrack/common/date-range'
import { sendAtLabel } from '@/lib/sendAt'

type Status = 'cho-lay' | 'dang-giao' | 'giao-ok' | 'hoan' | 'huy' | 'failed_shipment' | 'draft_failed' | 'cancelling' | string

interface Order {
  id: string
  /** Mã tham chiếu của shop (OT-…) — ngắn hơn mã hệ thống, dễ nhận ra */
  clientOrderId?: string
  channel: string
  cod: number
  status: string
  shipmentCode: string | null
  /** Bản ghi vận đơn (có cả khi BEST chưa cấp mã) — null = đơn không tạo vận đơn. */
  shipment?: { code: string | null } | null
  createdAt: string
  customer: { name: string; phone: string | null }
  /** Tiền của người bán trên đơn; null = đơn cũ chưa có sổ tiền. */
  money: OrderMoneyView | null
  /** Lần cuối tải file vận đơn — null = chưa tải */
  printedAt?: string | null
  /** Đơn hẹn gửi: giờ hẹn */
  sendAt?: string
}

interface OrderCounts {
  total: number
  byStatus: Partial<Record<string, number>>
  waybills: { total: number; printed: number; unprinted: number }
}

const PAGE_SIZE = 200

interface OrdersResponse {
  orders: Order[]
  nextCursor: string | null
}


export default function OrdersPage() {
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const [q, setQ] = useState('')
  // Bộ lọc trạng thái + thời gian giữ trên URL (F5 / gửi link vẫn giữ): ?status=cho-lay (từ ô "BEST chờ lấy"
  // ở Tổng quan) · ?range=7d · ?range=custom&from=2026-10-01&to=2026-10-11
  const [searchParams, setSearchParams] = useSearchParams()
  const statusParam = searchParams.get('status')
  const statusFilter = statusParam && (ORDER_FILTER_STATUSES as readonly string[]).includes(statusParam) ? statusParam : 'all'
  const [dateFilter, setDateFilter] = useDateFilterParams()
  const setStatusFilter = (s: string) =>
    setSearchParams(
      (p) => {
        const next = new URLSearchParams(p)
        if (s === 'all') next.delete('status')
        else next.set('status', s)
        return next
      },
      { replace: true },
    )
  /** Tham số lọc dùng chung cho danh sách và Xuất CSV */
  const filterParams = () => {
    const params = new URLSearchParams()
    if (statusFilter !== 'all') params.set('status', statusFilter)
    return applyDateFilter(params, dateFilter)
  }
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  // Đổi bộ lọc → bỏ chọn: đơn đã chọn có thể không còn trong danh sách, "đã chọn N" và in hàng loạt sẽ lệch
  // Lọc vận đơn đã tải / chưa tải — chọn tất cả khi lọc "Chưa tải" chỉ chọn đơn chưa tải
  const [printedFilter, setPrintedFilter] = useState<'all' | '0' | '1'>('all')
  useEffect(() => {
    setSelectedIds(new Set())
  }, [q, statusFilter, dateFilter.from, dateFilter.to, printedFilter])
  const [importOpen, setImportOpen] = useState(false)
  // Nhân viên (docs/KE_HOACH_THANH_VIEN_DON_VI.md): nút theo quyền chủ cấp — chủ đơn vị: mọi nút như trước
  const { can, seesMoney } = useUnitCan()
  const canCreate = can('orders.create')
  const canPrint = can('orders.print')
  const canShipPrepay = can('orders.ship_prepay')
  // /orders?import=1 (link cũ — nút "Tạo đơn bằng Excel" nay ở trang Tạo đơn) → mở hộp nhập file rồi bỏ tham số
  // (giữ các bộ lọc khác) để F5 / quay lại không tự mở lần nữa
  useEffect(() => {
    if (searchParams.get('import') !== '1') return
    if (canCreate) setImportOpen(true)
    setSearchParams(
      (p) => {
        const next = new URLSearchParams(p)
        next.delete('import')
        return next
      },
      { replace: true },
    )
  }, [searchParams, setSearchParams, canCreate])
  const [zipOpen, setZipOpen] = useState(false)
  // Đang chuẩn bị vận đơn đã chọn: in hay tải
  const [busy, setBusy] = useState<'print' | 'download' | null>(null)

  // Mỗi lần 200 đơn, "Xem thêm" tải tiếp theo cursor (trước chỉ hiện 50 đơn mới nhất)
  const { data, isLoading, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['orders', { q, status: statusFilter, from: dateFilter.from, to: dateFilter.to, printed: printedFilter }],
    queryFn: async ({ pageParam }) => {
      const params = filterParams()
      if (q) params.set('q', q)
      params.set('scope', 'self')
      params.set('limit', String(PAGE_SIZE))
      if (printedFilter !== 'all') params.set('printed', printedFilter)
      if (pageParam) params.set('cursor', pageParam)
      return get<OrdersResponse>(`/orders?${params.toString()}`)
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  })

  const orders = useMemo(() => data?.pages.flatMap((p) => p.orders) ?? [], [data])

  // Thống kê đầu trang: số đơn theo trạng thái (cùng bộ lọc ngày / tìm kiếm) + vận đơn đã tải / chưa tải
  const countsQ = useQuery({
    queryKey: ['order-counts', { q, from: dateFilter.from, to: dateFilter.to }],
    queryFn: () => {
      const params = applyDateFilter(new URLSearchParams(), dateFilter)
      if (q) params.set('q', q)
      return get<OrderCounts>(`/orders/counts?${params.toString()}`)
    },
    refetchInterval: 15_000,
  })
  const counts = countsQ.data

  const toggleSelectAll = () => {
    if (selectedIds.size === orders.length && orders.length > 0) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(orders.map((o) => o.id)))
    }
  }

  const toggleSelectOne = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  const selectedCodes = orders
    .filter((o) => selectedIds.has(o.id) && Boolean(o.shipmentCode))
    .map((o) => o.shipmentCode!)

  // Đơn "Chờ duyệt ship" đang chọn → gộp thành MỘT lệnh chuyển khoản (docs/KE_HOACH_SHOP_TRA_SHIP_QR.md §6)
  const selectedAwaiting = orders.filter((o) => selectedIds.has(o.id) && o.status === 'cho-duyet').map((o) => o.id)
  const [grouping, setGrouping] = useState(false)
  async function handleGroupShipPayment() {
    if (selectedAwaiting.length < 2) {
      toast.error('Chọn ít nhất 2 đơn "Chờ duyệt ship" để chuyển gộp')
      return
    }
    setGrouping(true)
    try {
      const r = await post<{ batch: { id: string } }>('/ship-payments/batches', { orderIds: selectedAwaiting })
      setSelectedIds(new Set())
      navigate(`/ship-payments/${r.batch.id}`)
    } catch (err) {
      toast.error((err as Error).message)
      refetch()
    } finally {
      setGrouping(false)
    }
  }

  /** Tải vận đơn về máy để tự in: ≤ 50 → một file PDF, nhiều hơn → zip (50 vận đơn / PDF), đúng thứ tự. */
  async function downloadWaybills(codes: string[]) {
    const pdf = codes.length <= 50
    await downloadFile('/shipping/waybills/download', codes.length === 1 ? `van-don_${codes[0]}.pdf` : `van-don_${codes.length}.${pdf ? 'pdf' : 'zip'}`, { codes })
    countsQ.refetch()
    refetch()
  }

  /** In / tải vận đơn các đơn đã chọn (đúng thứ tự trên danh sách). */
  async function handleBulk(kind: 'print' | 'download') {
    if (selectedCodes.length === 0) {
      toast.error('Các đơn được chọn chưa có mã vận đơn')
      return
    }
    setBusy(kind)
    try {
      if (kind === 'print') {
        await printWaybills(selectedCodes)
        countsQ.refetch()
        refetch()
      } else {
        await downloadWaybills(selectedCodes)
        toast.success(`Đã tải ${selectedCodes.length} vận đơn`)
      }
    } catch (err) {
      toast.error((err as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function printSingle(code: string) {
    try {
      await printWaybills([code])
      countsQ.refetch()
      refetch()
    } catch (err) {
      toast.error((err as Error).message)
    }
  }

  async function downloadSingle(code: string) {
    try {
      await downloadWaybills([code])
    } catch (err) {
      toast.error((err as Error).message)
    }
  }

  const allSelected = orders.length > 0 && selectedIds.size === orders.length

  // Xuất đúng những gì đang lọc (trạng thái + khoảng ngày); tên file ghi khoảng ngày
  async function exportCsv() {
    const qs = filterParams().toString()
    const span = [dateFilter.from, dateFilter.to].filter(Boolean).join('_')
    try {
      await downloadCsv(`/exports/orders.csv${qs ? `?${qs}` : ''}`, `orders${span ? `-${span}` : ''}.csv`)
    } catch (err) {
      toast.error((err as Error).message)
    }
  }

  const columns: DataTableColumn<Order>[] = [
    {
      key: 'id',
      priority: 'primary',
      header: (
        <span className="flex items-center gap-2.5">
          <Checkbox
            checked={allSelected}
            onChange={toggleSelectAll}
            aria-label={allSelected ? 'Bỏ chọn tất cả' : 'Chọn tất cả'}
          />
          Mã đơn
        </span>
      ),
      cell: (o) => (
        <span className="flex items-center gap-2.5">
          <Checkbox
            checked={selectedIds.has(o.id)}
            onChange={() => toggleSelectOne(o.id)}
            onClick={(e) => e.stopPropagation()}
            aria-label={`Chọn đơn ${o.id}`}
          />
          <Link to={`/orders/${o.id}`} title={o.id} className="motion-state font-mono text-sm text-accent hover:underline">
            {shortOrderId(o.id)}
          </Link>
        </span>
      ),
    },
    {
      key: 'customer',
      header: 'Khách hàng',
      priority: 'secondary',
      cell: (o) => (
        <span>
          {o.customer.name}
          {o.customer.phone && (
            <span className="font-mono text-muted"> · {phoneDisplay(o.customer.phone)}</span>
          )}
        </span>
      ),
    },
    { key: 'channel', header: 'Kênh', cell: (o) => o.channel },
    {
      key: 'cod',
      header: 'COD',
      align: 'right',
      cell: (o) => <span className="font-mono tabular-nums">{money(o.cod)}</span>,
    },
    // Không có quyền xem tiền: bỏ cột (máy chủ đã không trả sổ tiền)
    ...(seesMoney
      ? [{
          key: 'money',
          header: 'Thực nhận',
          align: 'right' as const,
          cell: (o: Order) => <OrderMoneyCell money={o.money} seller hasShipment={Boolean(o.shipmentCode || o.shipment || o.status === 'hen-gio')} />,
        }]
      : []),
    {
      key: 'status',
      header: 'Trạng thái',
      cell: (o) => (
        <span className="flex items-center gap-1.5 whitespace-nowrap">
          <StatusPill status={o.status} />
          {o.sendAt && <span className="font-mono text-xs tabular-nums text-inkSoft">{sendAtLabel(o.sendAt)}</span>}
        </span>
      ),
    },
    {
      key: 'shipment',
      header: 'Vận đơn',
      cell: (o) =>
        o.shipmentCode ? (
          <span className="flex items-center gap-2">
            <span className="font-mono text-xs">{o.shipmentCode}</span>
            {o.printedAt && (
              <span className="rounded bg-ok/10 px-1.5 py-0.5 text-xs font-medium text-ok" title={`Đã tải file vận đơn lần cuối ${new Date(o.printedAt).toLocaleString('vi-VN')}`}>
                Đã tải
              </span>
            )}
            {canPrint && (
              <>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    printSingle(o.shipmentCode!)
                  }}
                  className="motion-state shrink-0 whitespace-nowrap text-xs font-medium text-accent hover:underline"
                >
                  In
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    downloadSingle(o.shipmentCode!)
                  }}
                  className="motion-state shrink-0 whitespace-nowrap text-xs text-accent hover:underline"
                >
                  Tải PDF
                </button>
              </>
            )}
          </span>
        ) : (
          <span className="text-muted">—</span>
        ),
    },
    {
      key: 'createdAt',
      header: 'Tạo lúc',
      align: 'right',
      cell: (o) => (
        <span className="font-mono text-xs tabular-nums text-muted">
          {new Date(o.createdAt).toLocaleString('vi-VN')}
        </span>
      ),
    },
  ]

  return (
    <div className="space-y-5">
      <PageHeader
        title="Đơn hàng"
        actions={
          <>
            {canShipPrepay && selectedAwaiting.length > 0 && (
              <Button
                size="sm"
                onClick={handleGroupShipPayment}
                disabled={grouping || selectedAwaiting.length < 2}
              >
                {grouping ? 'Đang gộp…' : `Chuyển tiền ship gộp (${selectedAwaiting.length})`}
              </Button>
            )}
            {canPrint && selectedIds.size > 0 && (
              <>
                <Button size="sm" onClick={() => handleBulk('print')} disabled={busy != null || selectedCodes.length === 0}>
                  <Printer aria-hidden />
                  {busy === 'print' ? 'Đang chuẩn bị…' : `In ${selectedCodes.length} vận đơn`}
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleBulk('download')} disabled={busy != null || selectedCodes.length === 0}>
                  <Download aria-hidden />
                  {busy === 'download' ? 'Đang tải…' : `Tải ${selectedCodes.length} vận đơn`}
                </Button>
              </>
            )}
            {/* Chưa chọn đơn: in / tải vận đơn theo khoảng ngày tạo đơn (hộp WaybillZipDialog) */}
            {canPrint && selectedIds.size === 0 && (
              <Button variant="outline" size="sm" onClick={() => setZipOpen(true)}>
                <CalendarDays aria-hidden />
                Tải/In vận đơn
              </Button>
            )}
            {/* Tạo đơn nằm trên thanh đầu (DashboardLayout, GlassLayout); Tạo đơn bằng Excel trong trang Tạo đơn */}
            <Button variant="outline" size="sm" onClick={exportCsv}>
              Xuất CSV
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-72">
          <SearchInput
            aria-label="Tìm mã đơn, tên khách, kênh"
            placeholder="Tìm mã đơn, tên khách, kênh…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
        <label className="flex min-h-10 items-center gap-2 text-sm text-ink">
          <span className="text-muted">Vận đơn</span>
          <select
            value={printedFilter}
            onChange={(e) => setPrintedFilter(e.target.value as 'all' | '0' | '1')}
            className="h-10 rounded-md border border-rule bg-raise px-2 text-sm"
            aria-label="Lọc vận đơn đã tải / chưa tải"
          >
            <option value="all">Tất cả</option>
            <option value="0">Chưa tải{counts ? ` (${counts.waybills.unprinted.toLocaleString('vi-VN')})` : ''}</option>
            <option value="1">Đã tải{counts ? ` (${counts.waybills.printed.toLocaleString('vi-VN')})` : ''}</option>
          </select>
        </label>
      </div>

      {/* Lọc trạng thái bằng hàng ô số (01/11/2026 bỏ hàng nút Tất cả / Hẹn gửi… phía trên — trùng) */}
      {counts && <OrderStats counts={counts} value={statusFilter} onPick={setStatusFilter} />}

      {statusFilter === 'cho-duyet' && canShipPrepay && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-accent/40 bg-accent/5 px-4 py-2.5 text-sm text-ink">
          <Link to="/ship-payments" className="shrink-0 font-medium text-accent hover:underline">
            Tiền ship →
          </Link>
        </div>
      )}

      <div data-surface="solid" className="overflow-hidden rounded-xl bg-raise shadow-card">
        {isMobile ? (
          <OrderListMobile
            orders={orders}
            loading={isLoading}
            selectedIds={selectedIds}
            onToggle={toggleSelectOne}
            onOpen={(o) => navigate(`/orders/${o.id}`)}
          />
        ) : (
          <DataTable
            columns={columns}
            data={orders}
            rowKey={(o) => o.id}
            loading={isLoading}
            onRowClick={(o) => navigate(`/orders/${o.id}`)}
            emptyLabel={dateFilter.key !== 'all' ? 'Không có đơn' : 'Chưa có đơn'}
          />
        )}
        {orders.length > 0 && (
          <p className="border-t border-rule px-4 py-2.5 text-xs text-muted">
            {hasNextPage ? `Đang hiện ${orders.length.toLocaleString('vi-VN')} đơn mới nhất (còn nữa)` : `${orders.length.toLocaleString('vi-VN')} đơn`}
            {dateFilter.key !== 'all' && (dateFilter.from || dateFilter.to) && ` · tạo ${rangeSummary(dateFilter.from, dateFilter.to)}`}
            {selectedIds.size > 0 && ` · đã chọn ${selectedIds.size}`}
            {hasNextPage && (
              <button type="button" onClick={() => fetchNextPage()} disabled={isFetchingNextPage} className="ml-3 min-h-9 rounded-md px-2 font-medium text-accent hover:bg-accent/10">
                {isFetchingNextPage ? 'Đang tải…' : `Xem thêm ${PAGE_SIZE} đơn`}
              </button>
            )}
          </p>
        )}
      </div>

      {canCreate && (
        <Fab aria-label="Tạo đơn" data-dock-duplicate="" onClick={() => navigate('/orders/new')}>
          <Plus className="size-6" />
        </Fab>
      )}

      {/* Hộp nhập file tải sẵn sản phẩm / ngân hàng / kho khi mở trang — chỉ gắn khi được lên đơn */}
      {canCreate && <ImportOrdersModal open={importOpen} onClose={() => setImportOpen(false)} onSuccess={() => refetch()} />}
      {canPrint && <WaybillZipDialog open={zipOpen} onClose={() => setZipOpen(false)} initialFrom={dateFilter.from} initialTo={dateFilter.to} />}
    </div>
  )
}

// Chấm + chữ trạng thái màu đặc cho danh sách gọn (StatusPill dùng nền nhạt — làm chấm thì gần như vô hình)
const INTENT_DOT: Record<string, string> = { ok: 'bg-ok', warn: 'bg-warn', danger: 'bg-danger', info: 'bg-info', ship: 'bg-ship', neutral: 'bg-muted' }
const INTENT_TEXT: Record<string, string> = { ok: 'text-ok', warn: 'text-warn', danger: 'text-danger', info: 'text-info', ship: 'text-ship', neutral: 'text-inkSoft' }

/** "14:05" nếu hôm nay, còn lại "09/10" — đủ để nhận ra đơn trong danh sách ngắn. */
function shortWhen(iso: string): string {
  const d = new Date(iso)
  return d.toDateString() === new Date().toDateString()
    ? d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' })
}

/** Điện thoại: mỗi đơn một hàng gọn — chọn để in · tên khách · mã / giờ / trạng thái · COD. Bấm để mở đơn. */
function OrderListMobile({
  orders,
  loading,
  selectedIds,
  onToggle,
  onOpen,
}: {
  orders: Order[]
  loading: boolean
  selectedIds: Set<string>
  onToggle: (id: string) => void
  onOpen: (o: Order) => void
}) {
  if (loading) return <p className="px-4 py-6 text-center text-sm text-muted">Đang tải đơn…</p>
  if (!orders.length) return <p className="px-4 py-6 text-center text-sm text-muted">Chưa có đơn</p>
  return (
    <ul className="divide-y divide-rule/60">
      {orders.map((o, i) => {
        const meta = statusMeta(o.status)
        return (
          <li key={o.id} className="flex items-center gap-1 pl-3 pr-3">
            <Checkbox checked={selectedIds.has(o.id)} onChange={() => onToggle(o.id)} aria-label={`Chọn đơn ${o.id}`} />
            <button type="button" onClick={() => onOpen(o)} className="flex min-h-14 min-w-0 flex-1 items-center gap-3 py-2 pl-2 text-left">
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  <IndexBadge n={i + 1} />
                  <span className={cn('size-2 shrink-0 rounded-full', INTENT_DOT[meta.intent])} aria-hidden />
                  <span className="truncate text-sm font-medium text-ink">{o.customer.name}</span>
                </span>
                <span className="block truncate text-xs text-muted">
                  {/* Đơn hẹn gửi: giờ hẹn thay cho giờ tạo — dòng chật, giờ hẹn mới là thứ cần thấy */}
                  {shortOrderId(o.id)} · {o.sendAt ? '' : `${shortWhen(o.createdAt)} · `}<span className={cn('font-medium', INTENT_TEXT[meta.intent])}>{meta.text}{o.sendAt ? ` ${sendAtLabel(o.sendAt)}` : ''}</span>
                </span>
              </span>
              <span className="shrink-0 font-mono text-sm font-semibold tabular-nums text-ink">{money(o.cod)}</span>
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/** Thống kê đầu trang: tổng đơn, theo trạng thái (bấm để lọc), vận đơn đã tải / chưa tải. */
// Màu ô lọc = màu nhãn trạng thái trong bảng (StatusPill, theo intent của STATUS_LABEL); ô đang chọn đậm hơn + viền.
const TILE_TONE: Record<StatusIntent | 'total', { idle: string; on: string }> = {
  total: { idle: 'border-rule bg-raise text-ink', on: 'border-accent bg-accent/12 text-accent ring-1 ring-accent' },
  ok: { idle: 'border-ok/30 bg-ok/10 text-ok', on: 'border-ok bg-ok/20 text-ok ring-1 ring-ok' },
  warn: { idle: 'border-warn/30 bg-warn/10 text-warn', on: 'border-warn bg-warn/20 text-warn ring-1 ring-warn' },
  danger: { idle: 'border-danger/30 bg-danger/10 text-danger', on: 'border-danger bg-danger/20 text-danger ring-1 ring-danger' },
  info: { idle: 'border-info/30 bg-info/10 text-info', on: 'border-info bg-info/20 text-info ring-1 ring-info' },
  ship: { idle: 'border-ship/30 bg-ship/10 text-ship', on: 'border-ship bg-ship/20 text-ship ring-1 ring-ship' },
  neutral: { idle: 'border-rule bg-paperSoft text-inkSoft', on: 'border-inkSoft bg-inkSoft/15 text-ink ring-1 ring-inkSoft' },
}

function OrderStats({ counts, value, onPick }: { counts: OrderCounts; value: string; onPick: (s: string) => void }) {
  const n = (v: number | undefined) => (v ?? 0).toLocaleString('vi-VN')
  const tiles: Array<{ key: string; label: string; value: number | undefined }> = [
    { key: 'all', label: 'Tổng đơn', value: counts.total },
    ...ORDER_FILTER_STATUSES.map((s) => ({ key: s, label: statusMeta(s).text, value: counts.byStatus[s] })),
  ]
  // Ô gọn: nhãn và số cùng một dòng, rộng theo nội dung — không chiếm hai hàng ô to như lưới cũ.
  const tile = 'inline-flex items-baseline gap-2 rounded-lg border border-rule bg-raise px-3 py-1.5 mobile:py-2.5'
  return (
    <div className="flex flex-wrap gap-2">
      {tiles.map((t) => {
        const tone = TILE_TONE[t.key === 'all' ? 'total' : statusMeta(t.key).intent]
        const on = value === t.key
        return (
          <button
            key={t.key}
            type="button"
            aria-pressed={on}
            data-tone={t.key === 'all' ? 'total' : statusMeta(t.key).intent}
            onClick={() => onPick(t.key)}
            className={cn(tile, 'text-left hover:brightness-95', on ? tone.on : tone.idle)}
          >
            <span className="text-xs">{t.label}</span>
            <span className="font-mono text-base font-semibold tabular-nums">{n(t.value)}</span>
          </button>
        )
      })}
      <div className={tile}>
        <span className="text-xs text-muted">Vận đơn đã tải</span>
        <span className="font-mono text-base font-semibold tabular-nums text-ink">
          {n(counts.waybills.printed)}
          <span className="text-sm font-normal text-muted"> / {n(counts.waybills.total)}</span>
        </span>
        {counts.waybills.unprinted > 0 && <span className="text-xs text-warn">{n(counts.waybills.unprinted)} chưa tải</span>}
      </div>
    </div>
  )
}
